import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiRefreshCw,
  FiTarget,
  FiTrendingUp,
} from "react-icons/fi";
import { getPerformanceReview, getPerformanceReviews } from "../api/hrmApi";
import {
  normalizePerformanceReviewResponse,
  normalizePerformanceReviews,
} from "../api/hrmNormalizers";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";

const scoreFields = [
  ["productiveHoursDaily", "Productive Hours"],
  ["challengerLevel", "Challenger Level"],
  ["softSkills", "Soft Skills"],
  ["accountability", "Accountability"],
  ["credibility", "Credibility"],
  ["learnability", "Learnability"],
  ["professionalism", "Professionalism"],
  ["attentionToDetail", "Attention To Detail"],
  ["proactiveness", "Proactiveness"],
];

const statusOptions = ["All", "Green", "Amber", "Red"];
const PERFORMANCE_DESCRIPTION_START_DATE = "2026-05-01";

const toIsoDateKey = (value = "") => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }
  const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return isoMatch[0];
  }
  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(parsedDate.getDate()).padStart(2, "0")}`;
};

const shouldRenderPerformanceDescription = (review = {}) => {
  const safeReview = review || {};
  const reviewDateKey = toIsoDateKey(safeReview.rawReviewDate || safeReview.reviewDate);
  return Boolean(reviewDateKey && reviewDateKey >= PERFORMANCE_DESCRIPTION_START_DATE);
};

const safeRichTextUrl = (value = "") => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) {
    return "";
  }
  if (/^(https?:|mailto:|tel:)/i.test(trimmedValue)) {
    return trimmedValue;
  }
  if (trimmedValue.startsWith("/")) {
    return trimmedValue;
  }
  return "";
};

const escapeRichText = (value = "") =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sanitizePerformanceDescription = (value = "") => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }
  if (typeof window === "undefined" || !window.DOMParser) {
    return escapeRichText(rawValue).replace(/\n/g, "<br>");
  }
  if (!/<[a-z][\s\S]*>/i.test(rawValue)) {
    return escapeRichText(rawValue).replace(/\n/g, "<br>");
  }

  const body = new window.DOMParser().parseFromString(rawValue.replace(/<br\s*\/?>/gi, "<br>"), "text/html").body;
  body.querySelectorAll("script,style,iframe,object,embed,form,input,button").forEach((node) => node.remove());

  const hasMeaningfulContent =
    String(body.textContent || "").replace(/\u00a0/g, " ").trim() ||
    body.querySelector("img,video,audio,table,ul,ol,li,a");
  if (!hasMeaningfulContent) {
    return "";
  }

  body.querySelectorAll("*").forEach((node) => {
    const tagName = node.tagName?.toLowerCase();
    const safeHref = tagName === "a" ? safeRichTextUrl(node.getAttribute("href")) : "";
    const safeTextAlign = ["left", "center", "right", "justify"].includes(node.style?.textAlign)
      ? node.style.textAlign
      : "";
    const safeColor = /^#[0-9a-f]{3,6}$/i.test(node.style?.color || "") ? node.style.color : "";

    Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name));

    const safeStyles = [];
    if (safeTextAlign) {
      safeStyles.push(`text-align: ${safeTextAlign}`);
    }
    if (safeColor) {
      safeStyles.push(`color: ${safeColor}`);
    }
    if (safeStyles.length) {
      node.setAttribute("style", safeStyles.join("; "));
    }
    if (tagName === "a" && safeHref) {
      node.setAttribute("href", safeHref);
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noreferrer");
    }
  });

  return body.innerHTML;
};

const getScoreLabel = (score) => {
  if (score === null || score === undefined) {
    return "-";
  }
  return Number(score).toFixed(2).replace(/\.00$/, "");
};

const getScorePercent = (score) => {
  const numericScore = Number(score || 0);
  if (!Number.isFinite(numericScore)) {
    return 0;
  }
  return Math.max(0, Math.min(100, (numericScore / 5) * 100));
};

const getStatusTone = (status = "") => {
  const normalizedStatus = String(status).toLowerCase();
  if (normalizedStatus.includes("green")) {
    return "green";
  }
  if (normalizedStatus.includes("red")) {
    return "red";
  }
  return "amber";
};

const scoreTrendText = (currentScore, previousScore) => {
  if (currentScore === null || previousScore === null || previousScore === undefined) {
    return "No previous review";
  }
  const difference = currentScore - previousScore;
  if (Math.abs(difference) < 0.01) {
    return "Flat from last review";
  }
  return `${difference > 0 ? "+" : ""}${difference.toFixed(2)} from last review`;
};

const getAverageScore = (reviews = []) => {
  const scores = reviews
    .map((review) => review.overallScore)
    .filter((score) => score !== null && score !== undefined);
  if (!scores.length) {
    return null;
  }
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

const getSkillAverages = (reviews = []) =>
  scoreFields
    .map(([key, label]) => {
      const scores = reviews
        .map((review) => review.scores?.[key])
        .filter((score) => score !== null && score !== undefined);
      return {
        key,
        label,
        average: scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null,
      };
    })
    .filter((score) => score.average !== null)
    .sort((left, right) => right.average - left.average);

const getGraphDateLabel = (review = {}) =>
  String(review.reviewDate || review.rawReviewDate || "")
    .replace(/\s+\d{4}$/, "")
    .replace(",", "");

const PerformanceScoreGraph = ({ averageScore, reviews = [] }) => {
  const plotReviews = [...reviews]
    .filter((review) => review.overallScore !== null && review.overallScore !== undefined)
    .sort((left, right) => String(left.rawReviewDate || "").localeCompare(String(right.rawReviewDate || "")));
  const width = 720;
  const height = 210;
  const paddingX = 42;
  const paddingY = 24;
  const plotWidth = width - paddingX * 2;
  const plotHeight = height - paddingY * 2;
  const scoreValues = [
    ...plotReviews.map((review) => Number(review.overallScore || 0)),
    Number(averageScore || 0),
  ].filter((score) => Number.isFinite(score));
  const maxReturnedScore = Math.max(...scoreValues, 5);
  const axisMax = maxReturnedScore <= 5 ? 5 : Math.ceil(maxReturnedScore / 2) * 2;
  const axisTicks = Array.from({ length: 6 }, (_, index) => Math.round((axisMax - (axisMax / 5) * index) * 10) / 10);
  const scoreToY = (score) => {
    const normalizedScore = Math.max(0, Math.min(axisMax, Number(score || 0)));
    return paddingY + ((axisMax - normalizedScore) / axisMax) * plotHeight;
  };
  const points = plotReviews.map((review, index) => {
    const x = paddingX + (plotReviews.length > 1 ? (index / (plotReviews.length - 1)) * plotWidth : plotWidth / 2);
    return {
      id: review.id,
      label: getGraphDateLabel(review),
      reviewName: review.reviewName,
      score: review.overallScore,
      x,
      y: scoreToY(review.overallScore),
    };
  });
  const averageY = averageScore !== null && averageScore !== undefined ? scoreToY(averageScore) : null;

  return (
    <section className="new-hrm-panel new-hrm-performance-graph">
      <header className="new-hrm-panel__header new-hrm-panel__header--compact">
        <div>
          <h2>Score Trend</h2>
          <p>{plotReviews.length ? `${plotReviews.length} performance records` : "No score records returned"}</p>
        </div>
        <strong>{getScoreLabel(averageScore)} avg</strong>
      </header>
      {points.length ? (
        <>
          <div className="new-hrm-performance-graph__canvas">
            <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Performance score trend">
              {axisTicks.map((score) => (
                <g key={score}>
                  <line x1={paddingX} x2={width - paddingX} y1={scoreToY(score)} y2={scoreToY(score)} />
                  <text x="10" y={scoreToY(score) + 4}>{getScoreLabel(score)}</text>
                </g>
              ))}
              {averageY !== null && (
                <line className="new-hrm-performance-graph__average" x1={paddingX} x2={width - paddingX} y1={averageY} y2={averageY} />
              )}
              <polyline points={points.map((point) => `${point.x},${point.y}`).join(" ")} />
              {points.map((point) => (
                <g key={point.id || `${point.x}-${point.y}`}>
                  <circle cx={point.x} cy={point.y} r="5" />
                  <title>{`${point.reviewName}: ${getScoreLabel(point.score)}`}</title>
                </g>
              ))}
            </svg>
          </div>
          <div className="new-hrm-performance-graph__records">
            {points.map((point) => (
              <span key={point.id || point.label}>
                <strong>{getScoreLabel(point.score)}</strong>
                <small>{point.label || "Review"}</small>
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="new-hrm-empty-state">No performance scores available to plot.</p>
      )}
    </section>
  );
};

const PerformanceScreen = ({ viewUserId = "" }) => {
  const [status, setStatus] = useState("All");
  const [reviews, setReviews] = useState([]);
  const [employeeName, setEmployeeName] = useState("");
  const [totalReviews, setTotalReviews] = useState(0);
  const [selectedReviewId, setSelectedReviewId] = useState("");
  const [selectedReview, setSelectedReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const sortedReviews = useMemo(
    () =>
      [...reviews].sort((left, right) =>
        String(right.rawReviewDate || "").localeCompare(String(left.rawReviewDate || ""))
      ),
    [reviews]
  );

  const latestReview = sortedReviews[0] || null;
  const previousReview = sortedReviews[1] || null;
  const averageScore = useMemo(() => getAverageScore(reviews), [reviews]);
  const skillAverages = useMemo(() => getSkillAverages(reviews), [reviews]);
  const selectedDescriptionHtml = useMemo(
    () => (shouldRenderPerformanceDescription(selectedReview) ? sanitizePerformanceDescription(selectedReview?.description || "") : ""),
    [selectedReview?.description, selectedReview?.rawReviewDate, selectedReview?.reviewDate]
  );
  const strongestSkill = skillAverages[0];
  const focusSkill = skillAverages[skillAverages.length - 1];
  const statusSummary = useMemo(
    () =>
      reviews.reduce(
        (summary, review) => {
          summary[getStatusTone(review.status)] += 1;
          return summary;
        },
        { green: 0, amber: 0, red: 0 }
      ),
    [reviews]
  );

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getPerformanceReviews(viewUserId, {
        status: status === "All" ? "" : status,
        page: 1,
        pageSize: 50,
      });
      const normalizedPayload = normalizePerformanceReviews(payload);
      setReviews(normalizedPayload.reviews);
      setEmployeeName(normalizedPayload.employeeName);
      setTotalReviews(normalizedPayload.total);
      setSelectedReviewId((current) => {
        if (normalizedPayload.reviews.some((review) => review.id === current)) {
          return current;
        }
        return normalizedPayload.reviews[0]?.id || "";
      });
    } catch (loadError) {
      setReviews([]);
      setSelectedReviewId("");
      setSelectedReview(null);
      setError(loadError.message || "Performance reviews could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [status, viewUserId]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    let isActive = true;

    const selectedListReview = sortedReviews.find((review) => review.id === selectedReviewId) || null;
    setSelectedReview(selectedListReview);

    if (!selectedReviewId) {
      return () => {
        isActive = false;
      };
    }

    const loadReviewDetail = async () => {
      setDetailLoading(true);
      try {
        const payload = await getPerformanceReview(viewUserId, selectedReviewId);
        if (isActive) {
          setSelectedReview(normalizePerformanceReviewResponse(payload));
        }
      } catch {
        if (isActive) {
          setSelectedReview(selectedListReview);
        }
      } finally {
        if (isActive) {
          setDetailLoading(false);
        }
      }
    };

    loadReviewDetail();

    return () => {
      isActive = false;
    };
  }, [selectedReviewId, sortedReviews, viewUserId]);

  return (
    <section className="new-hrm-screen new-hrm-performance-screen">
      <ScreenHeader
        eyebrow="Feedback Reviews"
        title="Performance"
        description="Review history, score patterns, and improvement actions from Salesforce."
        actions={
          <button className="new-hrm-secondary-button" type="button" onClick={loadReviews}>
            <FiRefreshCw /> Refresh
          </button>
        }
      />

      <section className="new-hrm-panel new-hrm-performance-command new-hrm-loader-host">
        <ComponentLoader active={loading} label="Loading performance reviews" />
        <div className="new-hrm-performance-command__main">
          <span className={`new-hrm-performance-scoreplate new-hrm-performance-scoreplate--${getStatusTone(latestReview?.status)}`}>
            <small>Average Score</small>
            <strong>{getScoreLabel(averageScore)}</strong>
            <em>/ 5</em>
          </span>
          <div>
            <p>{employeeName || latestReview?.employeeName || "Current employee"}</p>
            <h2>{latestReview?.reviewName || "No feedback review found"}</h2>
            <span>
              {latestReview
                ? `${latestReview.status || "Amber"} review on ${latestReview.reviewDate || "date not set"}`
                : "Use the filters to check Salesforce feedback records."}
            </span>
          </div>
        </div>
        <div className="new-hrm-performance-command__insights">
          <span className="new-hrm-performance-insight">
            <FiTrendingUp />
            <small>Trend</small>
            <strong>{scoreTrendText(latestReview?.overallScore ?? null, previousReview?.overallScore ?? null)}</strong>
          </span>
          <span className="new-hrm-performance-insight">
            <FiCheckCircle />
            <small>Strength</small>
            <strong>{strongestSkill ? `${strongestSkill.label}: ${getScoreLabel(strongestSkill.average)}` : "No score fields populated"}</strong>
          </span>
          <span className="new-hrm-performance-insight">
            <FiTarget />
            <small>Focus</small>
            <strong>{focusSkill ? focusSkill.label : "No focus area yet"}</strong>
          </span>
        </div>
        <div className="new-hrm-performance-command__right">
          <div className="new-hrm-performance-mini-metrics">
            <span><small>Latest</small><strong>{getScoreLabel(latestReview?.overallScore)}</strong></span>
            <span><small>Reviews</small><strong>{totalReviews || reviews.length}</strong></span>
            <span><small>Status</small><strong>{latestReview?.status || "-"}</strong></span>
          </div>
          <div className="new-hrm-performance-filters">
            <label>
              <span>Status</span>
              <select value={status} onChange={(event) => setStatus(event.target.value)}>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </section>

      <PerformanceScoreGraph averageScore={averageScore} reviews={sortedReviews} />

      <section className="new-hrm-performance-layout">
        <section className="new-hrm-panel new-hrm-performance-timeline">
          <header className="new-hrm-panel__header new-hrm-panel__header--compact">
            <h2>Review Timeline</h2>
            <div className="new-hrm-performance-status-strip" aria-label="Review status summary">
              <span className="new-hrm-performance-status-strip__green">{statusSummary.green}</span>
              <span className="new-hrm-performance-status-strip__amber">{statusSummary.amber}</span>
              <span className="new-hrm-performance-status-strip__red">{statusSummary.red}</span>
            </div>
          </header>
          {error && (
            <p className="new-hrm-empty-state">
              <FiAlertTriangle /> {error}
            </p>
          )}
          {!error && !sortedReviews.length && !loading && (
            <p className="new-hrm-empty-state">No feedback reviews found for the selected filters.</p>
          )}
          <div className="new-hrm-performance-review-list">
            {sortedReviews.map((review) => (
              <button
                key={review.id}
                className={`new-hrm-performance-review-card${
                  selectedReviewId === review.id ? " new-hrm-performance-review-card--active" : ""
                }`}
                type="button"
                onClick={() => setSelectedReviewId(review.id)}
              >
                <span className={`new-hrm-performance-status new-hrm-performance-status--${getStatusTone(review.status)}`}>
                  {review.status || "Amber"}
                </span>
                <strong>{review.reviewName}</strong>
                <small>{review.reviewDate || "Review date not set"}</small>
                <em>{getScoreLabel(review.overallScore)} / 5</em>
              </button>
            ))}
          </div>
        </section>

        <section className="new-hrm-panel new-hrm-performance-detail new-hrm-loader-host">
          <ComponentLoader active={detailLoading} label="Loading review detail" />
          <header className="new-hrm-panel__header">
            <div>
              <h2>{selectedReview?.reviewName || "Review Detail"}</h2>
              <p>{selectedReview?.reviewDate || "Select a review to inspect scores and notes."}</p>
            </div>
            {selectedReview && (
              <div className="new-hrm-performance-detail__summary">
                <strong>{getScoreLabel(selectedReview.overallScore)}</strong>
                <span className={`new-hrm-performance-status new-hrm-performance-status--${getStatusTone(selectedReview.status)}`}>
                  {selectedReview.status}
                </span>
              </div>
            )}
          </header>

          {selectedReview ? (
            <>
              <div className="new-hrm-performance-score-grid">
                {scoreFields.map(([key, label]) => {
                  const score = selectedReview.scores?.[key] ?? null;
                  return (
                    <div key={key} className="new-hrm-performance-score-row">
                      <span>{label}</span>
                      <div>
                        <i style={{ width: `${getScorePercent(score)}%` }} />
                      </div>
                      <strong>{getScoreLabel(score)}</strong>
                    </div>
                  );
                })}
              </div>

              {selectedDescriptionHtml && (
                <div className="new-hrm-performance-description">
                  <h3>
                    <FiCheckCircle /> Description
                  </h3>
                  <div
                    className="new-hrm-project-rich-text new-hrm-performance-description__body"
                    dangerouslySetInnerHTML={{ __html: selectedDescriptionHtml }}
                  />
                </div>
              )}

              <div className="new-hrm-performance-actions">
                <h3>
                  <FiTarget /> Actions To Improve
                </h3>
                <p>{selectedReview.actionsToImprove || "No action items captured."}</p>
              </div>
            </>
          ) : (
            <p className="new-hrm-empty-state">Select a review from the timeline.</p>
          )}
        </section>
      </section>
    </section>
  );
};

export default PerformanceScreen;
